'use client';

import { defineMessages, useIntl } from 'react-intl';

import { useMediaType } from '@packages/shared/src/hooks/useMediaType/useMediaType';
import { useMediaTabletType } from '@packages/shared/src/hooks/useMediaTabletType/useMediaTabletType';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';

import { LoginButton } from '../../../../LoginButton/LoginButton';
import { LoginDialogComposed } from '../../../../Login/LoginDialogComposed/LoginDialogComposed';
import { useWishlistLink } from '../../shared/useWishlistLink';
import { Slot } from '../Base/Slot.client';
import type { SlotProps } from '../Base/Slot.client';

// Messages for popover content only - the title is now handled by the hook
const messages = defineMessages({
  overlayHeadline: {
    id: 'slot.wishlist.overlay.headline',
    defaultMessage: 'Du vermisst Artikel, die du dir gemerkt hast?',
  },
  overlayContent: {
    id: 'slot.wishlist.overlay.content',
    defaultMessage: 'Dann melde dich bitte an, um deine gespeicherten Artikel zu sehen.',
  },
});

const DesktopPopoverContent = ({ onLogin }: { onLogin: () => void }) => {
  const { formatMessage } = useIntl();
  return (
    <div className="flex flex-col p-2 pt-4">
      <h5 className="mb-2">{formatMessage(messages.overlayHeadline)}</h5>
      <p className="text-size-body-sm mb-2">{formatMessage(messages.overlayContent)}</p>
      <div className="self-end">
        <LoginButton className="w-full" onClick={onLogin} />
      </div>
    </div>
  );
};

/** Wishlist slot with count badge and login popover. On mobile devices the slot is a link and has no popover */
export const WishlistSlot = (props: Pick<SlotProps, 'priority'>) => {
  const { language } = useI18n();
  const isDesktop = useMediaType();
  const isTablet = useMediaTabletType();
  const { loggedIn } = useLoginState();
  const { href, label, badgeCount } = useWishlistLink();

  return (
    <LoginDialogComposed>
      {(openLoginDialog) => (
        <Slot
          {...props}
          language={language}
          icon={<span className="icon-emp-wishlist size-4" />}
          badgeCount={badgeCount}
          title={label}
          href={href}
        >
          {(isDesktop || isTablet) && !loggedIn && badgeCount === 0 && (
            <DesktopPopoverContent onLogin={openLoginDialog} />
          )}
        </Slot>
      )}
    </LoginDialogComposed>
  );
};
